/**
 * Dynamic Collection Navigation
 * Handles collection switching without full page reloads using Section Rendering API.
 * Syncs tab-collection and filter URL params with facets and pagination.
 */

class DynamicCollectionNavigation {
  constructor() {
    this.isLoading = false;
    this.abortController = null;
    this.currentRequestId = 0;
    this.pendingCollectionHandle = null;
    this.clickHandler = this.handleNavigationClick.bind(this);
    this.popStateHandler = this.handlePopState.bind(this);
    this.facetUpdateHandler = this.handleFacetUpdate.bind(this);
    this.urlParams = window.CollectionUrlParams;

    if (!document.querySelector('.collection-quick-navigation')) return;

    this.init();
  }

  init() {
    document.removeEventListener('click', this.clickHandler);
    document.addEventListener('click', this.clickHandler);

    window.removeEventListener('popstate', this.popStateHandler);
    window.addEventListener('popstate', this.popStateHandler);

    document.removeEventListener('facet:update', this.facetUpdateHandler, true);
    document.addEventListener('facet:update', this.facetUpdateHandler, true);

    this.refreshElementReferences();
    this.syncActiveStateFromUrl();

    const initialTab = this.urlParams.getSearchParams().get(this.urlParams.TAB_COLLECTION_KEY);
    if (initialTab) {
      this.urlParams.setActiveTabCollection(initialTab);
    }
  }

  refreshElementReferences() {
    this.collectionContainer = document.querySelector('#collection');
    this.collectionBanner = document.querySelector('[data-collection-banner]');
  }

  handleNavigationClick(event) {
    const link = event.target.closest('[data-collection-link]');

    if (!link || !link.closest('.collection-quick-navigation')) return;

    event.preventDefault();

    if (this.isLoading) return;

    const url = link.getAttribute('href');
    const collectionHandle = this.extractCollectionHandle(url);

    if (collectionHandle) {
      this.loadCollection(url, collectionHandle, { clearFilters: true });
      this.updateActiveState(link);
    }
  }

  handlePopState() {
    if (this.isLoading) return;

    const collectionHandle = this.urlParams.getActiveTabCollection();
    const link = collectionHandle
      ? document.querySelector(`[data-collection-link][href*="/collections/${collectionHandle}"]`)
      : null;

    if (collectionHandle && link) {
      this.loadCollection(link.href, collectionHandle, {
        preserveFilters: true,
        skipUrlUpdate: true
      });
      this.updateActiveState(link);
      return;
    }

    this.loadCollection(window.location.href, null, {
      preserveFilters: true,
      skipUrlUpdate: true
    });

    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const pageHandle = pathParts[0] === 'collections' ? pathParts[1] : null;

    if (pageHandle) {
      const pageLink = document.querySelector(`[data-collection-link][href*="/collections/${pageHandle}"]`);
      if (pageLink) this.updateActiveState(pageLink);
    }
  }

  handleFacetUpdate(event) {
    const hubHandle = this.urlParams.getHubCollectionHandle();
    const collectionHandle = this.urlParams.getActiveTabCollection();

    if (!collectionHandle || !event.detail?.url) return;

    if (hubHandle === 'frontpage' && collectionHandle !== hubHandle) {
      event.detail.url.pathname = `/collections/${hubHandle}`;
      event.detail.url.searchParams.set(this.urlParams.TAB_COLLECTION_KEY, collectionHandle);
    } else if (!event.detail.url.searchParams.has(this.urlParams.TAB_COLLECTION_KEY)) {
      event.detail.url.searchParams.set(this.urlParams.TAB_COLLECTION_KEY, collectionHandle);
    }
  }

  extractCollectionHandle(url) {
    try {
      const urlObj = new URL(url, window.location.origin);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);

      if (pathParts[0] === 'collections' && pathParts[1]) {
        return pathParts[1];
      }
    } catch (error) {
      // Silent fail
    }

    return null;
  }

  syncActiveStateFromUrl() {
    const collectionHandle = this.urlParams.getActiveTabCollection();
    if (!collectionHandle) return;

    const link = document.querySelector(
      `[data-collection-link][href*="/collections/${collectionHandle}"]`
    );

    if (link) {
      this.updateActiveState(link);
    }
  }

  async loadCollection(url, collectionHandle, options = {}) {
    const {
      clearFilters = false,
      preserveFilters = false,
      skipUrlUpdate = false
    } = options;

    if (this.isLoading && this.pendingCollectionHandle === collectionHandle) {
      return;
    }

    if (this.isLoading) {
      this.abortController?.abort();
    }

    const requestId = ++this.currentRequestId;
    this.isLoading = true;
    this.pendingCollectionHandle = collectionHandle;
    this.abortController = new AbortController();
    this.urlParams.setActiveTabCollection(collectionHandle);

    try {
      this.showLoadingState();

      const sectionsToRender = this.getSectionsToRender();

      if (sectionsToRender.length === 0) {
        throw new Error('No sections found to render');
      }

      if (!skipUrlUpdate) {
        this.urlParams.updateBrowserUrl(collectionHandle, { clearFilters });
      }

      const fetchUrl = this.urlParams.buildSectionFetchUrl(
        sectionsToRender.map((section) => section.id),
        collectionHandle,
        { preserveFilters: preserveFilters || !clearFilters }
      );

      const response = await fetch(fetchUrl, {
        signal: this.abortController.signal,
        cache: 'no-store',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (requestId !== this.currentRequestId) return;

      await this.updateSections(data, sectionsToRender);

      if (requestId !== this.currentRequestId) return;

      this.refreshElementReferences();
      this.syncActiveStateFromUrl();
      this.dispatchCollectionChangeEvent(collectionHandle);
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.currentRequestId) {
        return;
      }

      this.showError();

      setTimeout(() => {
        window.location.href = url;
      }, 1000);
    } finally {
      if (requestId === this.currentRequestId) {
        this.isLoading = false;
        this.pendingCollectionHandle = null;
        this.hideLoadingState();
      }
    }
  }

  getSectionsToRender() {
    const sections = [];

    const bannerSection = document.querySelector('[id^="shopify-section-"][id*="collection-banner"]');
    if (bannerSection) {
      sections.push({
        id: bannerSection.id.replace('shopify-section-', ''),
        element: bannerSection,
        type: 'banner'
      });
    }

    let mainSection = document.querySelector('.shopify-section--main-collection');

    if (!mainSection) {
      mainSection = document.querySelector('[id^="shopify-section-template"][id*="__main"]:not([id*="__main-"])');
    }

    if (!mainSection) {
      const containerElement = document.querySelector('[data-collection-container]');
      if (containerElement) {
        mainSection = containerElement.closest('[id^="shopify-section-"]');
      }
    }

    if (mainSection) {
      sections.push({
        id: mainSection.id.replace('shopify-section-', ''),
        element: mainSection,
        type: 'main'
      });
    }

    document.querySelectorAll('.shopify-section--collection-products').forEach((section) => {
      sections.push({
        id: section.id.replace('shopify-section-', ''),
        element: section,
        type: 'products'
      });
    });

    return sections;
  }

  updateSections(data, sectionsToRender) {
    const updates = sectionsToRender.map((section) => {
      const sectionData = data[section.id];

      if (!sectionData || !section.element) {
        return Promise.resolve();
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionData, 'text/html');
      const newSectionElement = doc.querySelector(`#shopify-section-${section.id}`);

      if (!newSectionElement) {
        return Promise.resolve();
      }

      const currentSection = section.element;
      const currentHeight = currentSection.offsetHeight;

      currentSection.style.minHeight = `${currentHeight}px`;
      currentSection.innerHTML = newSectionElement.innerHTML;

      Array.from(newSectionElement.attributes).forEach((attr) => {
        if (attr.name !== 'id') {
          currentSection.setAttribute(attr.name, attr.value);
        }
      });

      const sectionStylesheet = doc.querySelector('style[data-section-stylesheet]');
      if (sectionStylesheet && !currentSection.querySelector('style[data-section-stylesheet]')) {
        currentSection.prepend(sectionStylesheet.cloneNode(true));
      }

      this.reinitializeSection(currentSection);

      requestAnimationFrame(() => {
        currentSection.style.minHeight = '';
      });

      return Promise.resolve();
    });

    return Promise.all(updates);
  }

  reinitializeSection(section) {
    document.dispatchEvent(
      new CustomEvent('section:loaded', {
        detail: { section }
      })
    );

    if (typeof Shopify !== 'undefined' && Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }

    section.querySelectorAll('script').forEach((script) => {
      if (script.src) return;

      try {
        const executableScript = document.createElement('script');
        executableScript.textContent = script.textContent;
        script.replaceWith(executableScript);
      } catch (error) {
        // Silent fail
      }
    });

    if (typeof theme !== 'undefined' && theme.reload) {
      theme.reload();
    }
  }

  updateActiveState(activeLink) {
    const quickNavigation = activeLink.closest('.collection-quick-navigation');
    if (!quickNavigation) return;

    quickNavigation.querySelectorAll('[data-collection-link]').forEach((link) => {
      link.classList.remove('is-selected');
      link.setAttribute('aria-current', 'false');
    });

    activeLink.classList.add('is-selected');
    activeLink.setAttribute('aria-current', 'page');
  }

  showLoadingState() {
    document.body.classList.add('collection-loading');

    const loadingBar = document.querySelector('loading-bar');
    if (loadingBar) {
      loadingBar.setAttribute('loading', '');
    }

    document.querySelectorAll('.collection-quick-navigation').forEach((navigation) => {
      navigation.style.pointerEvents = 'none';
      navigation.style.opacity = '0.6';
    });

    const container = document.querySelector('#collection');
    if (!container || container.querySelector('.collection-loading-overlay')) return;

    container.style.position = 'relative';
    container.style.minHeight = '400px';

    const overlay = document.createElement('div');
    overlay.className = 'collection-loading-overlay';
    overlay.innerHTML = `
      <div class="collection-loading-spinner">
        <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" stroke-width="3" opacity="0.2"/>
          <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" stroke-width="3"
                  stroke-dasharray="90" stroke-dashoffset="0" stroke-linecap="round">
            <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20"
                              dur="1s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
    `;
    container.appendChild(overlay);
  }

  hideLoadingState() {
    document.body.classList.remove('collection-loading');

    const loadingBar = document.querySelector('loading-bar');
    if (loadingBar) {
      loadingBar.removeAttribute('loading');
    }

    document.querySelectorAll('.collection-quick-navigation').forEach((navigation) => {
      navigation.style.pointerEvents = '';
      navigation.style.opacity = '';
    });

    const overlay = document.querySelector('.collection-loading-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  showError() {
    const toast = document.querySelector('x-toast');
    if (!toast) return;

    const message = document.createElement('div');
    message.className = 'toast__message toast__message--error';
    message.textContent = 'Failed to load collection. Redirecting...';
    toast.appendChild(message);

    setTimeout(() => message.remove(), 3000);
  }

  dispatchCollectionChangeEvent(collectionHandle) {
    window.dispatchEvent(
      new CustomEvent('collection:changed', {
        detail: {
          collectionHandle,
          timestamp: Date.now()
        }
      })
    );
  }
}

let dynamicCollectionNavigationInstance = null;

function initDynamicCollectionNavigation() {
  if (!document.querySelector('.collection-quick-navigation')) return;

  dynamicCollectionNavigationInstance = new DynamicCollectionNavigation();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDynamicCollectionNavigation);
} else {
  initDynamicCollectionNavigation();
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initDynamicCollectionNavigation);
}
